<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="title"
    :agree-text="agreeText"
    dialog-width="60%"
    min-body-height="50vh"
    header-shadow
    :loading="loading"
    :is-valid="isValid"
    @agree="agree"
  >
    <template #body>
      <v-col cols="12">
        <v-form v-model="isValid" ref="form">
          <v-row no-gutters align="center">
            <v-col cols="4">
              <data-input v-model="configuration.name" v-bind="nameBind" />
            </v-col>
          </v-row>
          <v-row no-gutters class="pt-7">
            <v-col cols="12">
              <draggable-list
                :value.sync="configuration.tree"
                allow-one-element-on-main-level
                allow-nested-groups
                disable-group-adding
                fill-width
                disable-borders
                child-items-key="children"
              >
                <template #listElement="item">
                  <v-row no-gutters class="flex-nowrap treeview-element mb-2 px-2" align="center">
                    <v-col cols="auto">
                      <v-icon style="cursor: pointer">
                        mdi-drag-vertical
                      </v-icon>
                    </v-col>
                    <v-col cols="4" class="px-2">
                      <data-input
                        v-model="item.layer"
                        v-bind="layerBind"
                        @input="setItemKeyValue(item, 'layer', $event)"
                        class="treeview-input"
                      />
                    </v-col>
                    <v-col cols="4" class="px-2" v-if="item.level > 1">
                      <data-input
                        v-model="item.child_attribute"
                        v-bind="attributeBind"
                        :disabled="!item.layer"
                        :items="getLayerAttributes(item.layer)"
                        @input="setItemKeyValue(item, 'child_attribute', $event)"
                        class="treeview-input"
                      />
                    </v-col>
                    <v-col cols="auto" class="pl-1">
                      <v-icon @click="openSettingsDialog(item)" color="primary">
                        mdi-cog
                      </v-icon>
                    </v-col>
                    <v-col cols="auto" class="pl-1" v-if="item.level > 1">
                      <v-icon
                        @click="deleteElement(item)"
                        color="error"
                        :disabled="item.level === 2 && configuration.tree[0].children.length <= 1"
                      >
                        mdi-close
                      </v-icon>
                    </v-col>
                    <v-spacer />
                    <v-col cols="auto">
                      <base-rounded-button
                        icon="mdi-plus"
                        size="x-small"
                        color="success"
                        @click="addElement(item)"
                        :tooltip="$i18n.t('admin.treeviews.addElement')"
                      />
                    </v-col>
                  </v-row>
                </template>
              </draggable-list>
            </v-col>
          </v-row>
        </v-form>
        <main-input-dialog
          v-if="isSettingsDialogLoaded"
          :is-visible.sync="isSettingsDialogVisible"
          :title-translation="$i18n.t('admin.treeviews.elementSettings.elementSettings')"
          agree-text="dialog.agreeSave"
          dialog-width="55%"
          @agree="saveSettings"
          :inputs="settingsDialogInputs"
        >
          <template v-for="(input, idx) of settingsDialogInputsNames" #[`${input}.input`]="data">
            <v-col :cols="12 - data.leftCols" :key="idx">
              <v-row no-gutters align="center" class="pb-5">
                <v-col cols="1" />
                <v-col :cols="data.cols">
                  <data-input v-model="currentSettings[input]" v-bind="data" />
                </v-col>
              </v-row>
            </v-col>
          </template>
          <template #badges.input="data">
            <v-col :cols="data.cols">
              <v-row no-gutters v-for="(badge, idx) of currentSettings.badges" :key="idx" class="pb-4">
                <v-col cols="12">
                  <v-row no-gutters>
                    <v-col cols="1" />
                    <v-col cols="11">
                      <v-radio-group
                        height="16px"
                        class="my-0 py-0 treeview-radio"
                        v-model="badge.type"
                        @change="onBadgeTypeChanged(idx, $event)"
                        dense
                        row
                        hide-details
                      >
                        <v-radio :label="$i18n.t('admin.treeviews.elementSettings.color')" value="color"></v-radio>
                        <v-radio
                          :label="$i18n.t('admin.treeviews.elementSettings.attribute')"
                          value="attribute"
                        ></v-radio>
                      </v-radio-group>
                    </v-col>
                  </v-row>
                  <v-row no-gutters align="center">
                    <v-col cols="1" style="height: 51px">
                      <data-input v-if="badge.type === 'color'" v-model="badge.badge_color" v-bind="badgeColorBind" />
                    </v-col>
                    <v-col :cols="badge.type === 'attribute' ? 5 : 10">
                      <data-input v-model="badge.badge_text" v-bind="badgeValueBind" />
                    </v-col>
                    <v-col cols="5" v-if="badge.type === 'attribute'" class="pl-1">
                      <data-input v-model="badge.badge_color_attribute" v-bind="badgeAttributeBind" />
                    </v-col>
                    <v-col cols="1">
                      <v-icon color="error" @click="deleteBadge(idx)">mdi-close</v-icon>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="1" />
                <v-col cols="11">
                  <v-radio-group
                    height="16px"
                    class="my-0 py-0 treeview-radio"
                    v-model="newBadge.type"
                    @change="onBadgeTypeChanged('new', $event)"
                    dense
                    row
                    hide-details
                  >
                    <v-radio :label="$i18n.t('admin.treeviews.elementSettings.color')" value="color"></v-radio>
                    <v-radio :label="$i18n.t('admin.treeviews.elementSettings.attribute')" value="attribute"></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row no-gutters align="center">
                <v-col cols="1" style="height: 51px">
                  <data-input v-if="newBadge.type === 'color'" v-model="newBadge.badge_color" v-bind="badgeColorBind" />
                </v-col>
                <v-col :cols="newBadge.type === 'attribute' ? 5 : 10">
                  <data-input v-model="newBadge.badge_text" v-bind="badgeValueBind" />
                </v-col>
                <v-col cols="5" v-if="newBadge.type === 'attribute'" class="pl-1">
                  <data-input v-model="newBadge.badge_color_attribute" v-bind="badgeAttributeBind" />
                </v-col>
                <v-col cols="1">
                  <v-icon color="success" @click="addBadge">mdi-check</v-icon>
                </v-col>
              </v-row>
            </v-col>
          </template>
        </main-input-dialog>
      </v-col>
    </template>
  </main-large-dialog>
</template>
<script>
import { get } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import DraggableList from '@/components/DraggableList';
import MainInputDialog from '@/components/MainInputDialog';
import MainLargeDialog from '@/components/MainLargeDialog';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'TreeviewSettingsDialog',
  components: { DataInput, DraggableList, MainInputDialog, MainLargeDialog },
  mixins: [prepareAttributesFeatureCardMixin],
  props: {
    configuration: {
      type: Object,
      default: () => {
        return {};
      },
    },
    isVisible: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    agreeText: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
  },
  computed: {
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    layersArray() {
      return this.$_objectToArray(this.layers)
        .filter(layer => /* layer.layer_scope === 'core' && */ layer.type === 'features_layer')
        .map(layer => {
          return { text: layer.name, value: layer.id };
        });
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },

    nameBind() {
      return {
        dataType: { name: 'text' },
        label: this.$i18n.t('admin.treeviews.configurationName'),
        hideDetails: true,
        rules: this.requiredRules,
      };
    },
    attributeBind() {
      return {
        dataType: { name: 'select' },
        label: this.$i18n.t('admin.treeviews.attribute'),
        hideDetails: true,
        rules: this.requiredRules,
      };
    },
    layerBind() {
      return {
        dataType: { name: 'select' },
        items: this.layersArray,
        label: this.$i18n.t('dialog.layer'),
        hideDetails: true,
        rules: this.requiredRules,
      };
    },
    badgeValueBind() {
      return {
        dataType: { name: 'text' },
        hideDetails: true,
      };
    },
    badgeColorBind() {
      return {
        dataType: { name: 'color' },
      };
    },
    badgeAttributeBind() {
      return {
        dataType: { name: 'select' },
        items: this.getLayerDictAttributes(this.currentElement?.layer),
        label: this.$i18n.t('admin.treeviews.elementSettings.dictAttribute'),
        hideDetails: true,
      };
    },

    settingsDialogInputs() {
      return [
        {
          name: 'title',
          translationPath: 'admin.treeviews.elementSettings.title',
          dataType: {
            name: 'text',
          },
          hideDetails: true,
          leftCol: '5',
          cols: '7',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'subtitle',
          translationPath: 'admin.treeviews.elementSettings.subtitle',
          subtitle: 'default.optional',
          dataType: {
            name: 'text',
          },
          hideDetails: true,
          leftCol: '5',
          cols: '7',
        },
        {
          name: 'badges',
          translationPath: 'admin.treeviews.elementSettings.badges',
          subtitle: 'admin.treeviews.elementSettings.badgesSubtitle',
          leftCol: '5',
          cols: '7',
        },
      ];
    },
    settingsDialogInputsNames() {
      return this.settingsDialogInputs.map(input => input.name);
    },
    settingsDialogSpecialInputsNames() {
      return this.settingsDialogInputs.filter(input => !input.dataType).map(input => input.name);
    },

    requiredRules() {
      return [v => !!v || v === 0 || 'rules.required'];
    },
  },
  data: () => ({
    isValid: false,
    isSettingsDialogVisible: false,
    isSettingsDialogLoaded: false,

    currentElement: undefined,
    currentSettings: {},
    newBadge: {
      badge_text: '',
      type: 'color',
      badge_color: '#000000',
      badge_color_attribute: null,
    },
  }),
  methods: {
    agree() {
      const orderElementsInTree = tree => {
        const fn = (elements, indexPlus1 = false) => {
          return elements.map((element, index) => {
            return {
              ...element,
              order: indexPlus1 ? index + 1 : index,
              ...(element.children ? { children: fn(element.children, true) } : {}),
            };
          });
        };
        return fn(tree);
      };

      let { name, tree, id } = this.configuration;
      tree = orderElementsInTree(tree);
      const configuration = JSON.parse(JSON.stringify({ name, ...tree[0], ...(id ? { id } : {}) })); // Formater frontend -> backend
      this.$emit('agree', configuration);
    },
    addElement(item) {
      let { currentItem } = this.getItem(item);
      let idAttrName;
      if (item.layer || item.layer === 0) {
        const dSName = this.layers[item.layer]?.data_source_name || '';
        idAttrName = this.dataSources[dSName]?.attributes_schema.id_name || '';
      }
      this.$set(currentItem, 'children', [
        ...currentItem.children,
        {
          layer: null,
          child_attribute: null,
          title: '',
          subtitle: '',
          badges: [],
          children: [],
          parent_attribute: idAttrName,
        },
      ]);
    },
    deleteElement(item) {
      let { currentItem, keyToDeleteValuesLast } = this.getItem(item, true);
      this.$delete(currentItem.children, keyToDeleteValuesLast);
    },

    getItem(item, deleting = false) {
      const keyToDeleteValues = (item?.elementKey || '')
        .split('-')
        .map(item => parseInt(item))
        .splice(1);
      let keyToDeleteValuesLast;
      if (deleting) keyToDeleteValuesLast = keyToDeleteValues.pop();
      let currentItem = { children: this.configuration.tree };
      keyToDeleteValues.forEach(keyValue => {
        currentItem = (currentItem?.children || [])[keyValue];
      });
      return { currentItem, keyToDeleteValuesLast };
    },

    setItemKeyValue(item, key, value) {
      let { currentItem } = this.getItem(item);
      this.$set(currentItem, key, JSON.parse(JSON.stringify(value)));
      if (key === 'layer') {
        const dSName = this.layers[value]?.data_source_name || '';
        this.$set(currentItem, 'child_attribute', null);
        this.$set(currentItem, 'datasource', dSName);
        if (currentItem.children) {
          const idAttrName = this.dataSources[dSName]?.attributes_schema.id_name || '';
          this.$set(
            currentItem,
            'children',
            currentItem.children.map(child => {
              return { ...child, parent_attribute: idAttrName };
            })
          );
        }
      }
      this.$refs.form?.resetValidation();
    },

    addBadge() {
      this.currentSettings.badges.push(JSON.parse(JSON.stringify(this.newBadge)));
      this.newBadge = {
        badge_text: '',
        type: 'color',
        badge_color: '#000000',
        badge_color_attribute: null,
      };
    },
    deleteBadge(badgeIdx) {
      this.$delete(this.currentSettings.badges, badgeIdx);
    },
    onBadgeTypeChanged(badgeIdx, value) {
      let badge = badgeIdx === 'new' ? this.newBadge : this.currentSettings.badges[badgeIdx];
      if (value === 'color') {
        this.$set(badge, 'badge_color', '#000000');
        this.$set(badge, 'badge_color_attribute', null);
      } else if (value === 'attribute') {
        this.$set(badge, 'badge_color_attribute', '');
        this.$set(badge, 'badge_color', null);
      }
    },

    getLayerAttributes(layerId) {
      if (!layerId && layerId !== 0) return [];
      else {
        return this.$_flattenStructure(
          this.getForm(
            this.layers[layerId]?.form_schema?.elements || [],
            this.dataSources[this.layers[layerId].data_source_name]?.attributes_schema?.attributes || [],
            { returnDictRelationInfo: true }
          )
        ).map(attr => {
          return { text: attr.label, value: attr.attribute };
        });
      }
    },
    getLayerDictAttributes(layerId) {
      if (!layerId && layerId !== 0) return [];
      else {
        return this.$_flattenStructure(
          this.getForm(
            this.layers[layerId]?.form_schema?.elements || [],
            this.dataSources[this.layers[layerId].data_source_name]?.attributes_schema?.attributes || [],
            { returnDictRelationInfo: true }
          )
        )
          .filter(attr => attr.isDict)
          .map(attr => {
            return { text: attr.label, value: attr.id }; // attribute / id
          });
      }
    },

    openSettingsDialog(item) {
      this.currentElement = item;
      const { title, subtitle, badges = [] } = JSON.parse(JSON.stringify(item || {}));
      this.currentSettings = {
        title,
        subtitle,
        badges: badges.map(badge => {
          if (badge['badge_color'] || badge['badge_color'] === '') badge.type = 'color';
          else if (badge['badge_color_attribute'] || badge['badge_color_attribute'] === '') badge.type = 'attribute';
          return badge;
        }),
      };
      this.isSettingsDialogLoaded = true;
      this.isSettingsDialogVisible = true;
    },
    saveSettings() {
      let { currentItem } = this.getItem(this.currentElement);

      const currentSettings = JSON.parse(JSON.stringify(this.currentSettings));
      currentSettings['badges'] = (currentSettings.badges || []).map(badge => {
        delete badge.type;
        return badge;
      });
      Object.keys(currentSettings).forEach(key => {
        this.$set(currentItem, key, currentSettings[key]);
      });
      this.isSettingsDialogVisible = false;
    },
  },
  watch: {
    isSettingsDialogVisible(nV) {
      if (!nV) {
        this.currentElement = undefined;
        this.currentSettings = {};
        this.newBadge = {
          badge_text: '',
          type: 'color',
          badge_color: '#000000',
          badge_color_attribute: null,
        };
      }
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .treeview-element {
    height: 51px;
    background-color: rgba(0, 0, 0, 0.02);
  }
  .treeview-input {
    .v-input__slot {
      min-height: 32px !important;
    }
    .v-input__append-inner {
      margin-top: 4px !important;
    }
    .v-label {
      font-size: 16px;
      top: 7px !important;
    }
  }
  .treeview-radio {
    .v-icon {
      font-size: 15px;
    }
    .v-label {
      font-size: 12px;
    }
    .v-input--selection-controls__ripple {
      width: 15px;
      height: 15px;
      left: -3px;
      top: calc(50% - 15px) !important;
    }
    .v-input--selection-controls__input {
      margin-right: 0;
    }
  }
}
</style>
